/**
 * Login with Code page — 6-digit code entry (MFA step 2).
 *
 * After successful credential validation on the Login page, the BFF
 * emails a 6-digit code. This page collects and validates that code.
 * The input auto-submits once exactly 6 digits have been entered.
 *
 * @module pages/loginWithCode
 */

import React, { useState, useRef, useEffect } from 'react';
import { useNavigate }                         from 'react-router-dom';
import Layout        from '../components/Layout';
import { useI18n }   from '../dataProviders/I18nProvider';
import { useAuth }   from '../hooks/useAuth';
import apiClient     from '../procedural/apiClient';
import config        from '../config';
import * as constants from '../constants';

/**
 * Extract up to constants.CODE_LENGTH digits from a raw string.
 *
 * @param {string} raw - Unfiltered input value.
 * @returns {string} Digits only, capped at constants.CODE_LENGTH.
 */
function sanitize(raw) {
  return raw.replace(/\D/g, '').slice(0, constants.CODE_LENGTH);
}

/**
 * Renders the login-with-code form: a single numeric input that
 * auto-submits when the user enters exactly 6 digits.
 *
 * @returns {React.ReactElement}
 */
export default function LoginWithCode() {
  const { t }                          = useI18n();
  const { isAuthenticated, setUser, scheduleRefresh } = useAuth();
  const navigate                       = useNavigate();
  const submittingRef                  = useRef(false);
  const [code, setCode]                = useState('');
  const [error, setError]              = useState(null);
  const [submitting, setSubmitting]    = useState(false);

  /* ---- Guards --------------------------------------------------------- */

  useEffect(() => {
    if (isAuthenticated) {
      navigate(config.pagePaths.dashboard, { replace: true });
      return;
    }
    if (!sessionStorage.getItem(constants.SK_LOGIN_USER_ID)) {
      navigate(config.pagePaths.login, { replace: true });
    }
  }, [isAuthenticated, navigate]);

  /* ---- Code submission ------------------------------------------------ */

  /**
   * Submit the 6-digit code to POST /auth/login-with-code.
   *
   * @param {string} codeValue - The sanitized 6-digit code.
   */
  async function submitCode(codeValue) {
    if (submittingRef.current) return;
    submittingRef.current = true;
    setError(null);
    setSubmitting(true);

    const userId  = sessionStorage.getItem(constants.SK_LOGIN_USER_ID);
    const emailId = sessionStorage.getItem(constants.SK_LOGIN_EMAIL_ID);
    if (!userId) {
      setError(t('loginWithCode.error.generic'));
      setSubmitting(false);
      submittingRef.current = false;
      return;
    }

    try {
      const res = await apiClient.post('/auth/login-with-code', {
        userId,
        code: codeValue,
        emailId,
      });
      sessionStorage.removeItem(constants.SK_LOGIN_USER_ID);
      sessionStorage.removeItem(constants.SK_LOGIN_EMAIL_ID);
      setUser(res.data.user);
      scheduleRefresh(res.data.sessionTTL);
      navigate(config.pagePaths.dashboard);
    } catch (err) {
      const errorCode = err.response?.data?.error;
      if (errorCode === 'invalidCode') {
        setError(t('loginWithCode.error.invalidCode'));
        setTimeout(() => {
          navigate(config.pagePaths.home);
        }, 3000);
      } else {
        setError(t('loginWithCode.error.generic'));
      }
      setCode('');
      setSubmitting(false);
      submittingRef.current = false;
    }
  }

  /* ---- Input handlers ------------------------------------------------- */

  /**
   * Handle typing. Filters to digits, caps at constants.CODE_LENGTH,
   * and auto-submits when the code reaches full length.
   *
   * @param {React.ChangeEvent<HTMLInputElement>} e
   */
  function handleChange(e) {
    const digits = sanitize(e.target.value);
    setCode(digits);

    if (digits.length === constants.CODE_LENGTH) {
      submitCode(digits);
    }
  }

  /**
   * Handle paste. Extracts digits from the pasted text and uses
   * only the first constants.CODE_LENGTH digits.
   *
   * @param {React.ClipboardEvent<HTMLInputElement>} e
   */
  function handlePaste(e) {
    e.preventDefault();
    const pasted = sanitize(e.clipboardData.getData('text'));
    setCode(pasted);

    if (pasted.length === constants.CODE_LENGTH) {
      submitCode(pasted);
    }
  }

  /* ---- Render --------------------------------------------------------- */

  /** Inline styles specific to the code input field. */
  const codeInputStyle = {
    textAlign:     'center',
    fontSize:      '20px',
    letterSpacing: '8px',
  };

  return (
    <Layout title={t('page.login')}>
      <div className="auth-container">
        <div className="auth-card">
          <div className="auth-form">
            {error && <div className="auth-error">{error}</div>}

            <p className="auth-prompt">
              {t('loginWithCode.prompt')}
            </p>

            <div className="auth-field">
              <input
                className="auth-input"
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                maxLength={constants.CODE_LENGTH}
                autoComplete="one-time-code"
                autoFocus
                value={code}
                onChange={handleChange}
                onPaste={handlePaste}
                disabled={submitting}
                placeholder={t('loginWithCode.placeholder')}
                style={codeInputStyle}
              />
            </div>

            {submitting && (
              <p className="auth-status">{t('loginWithCode.verifying')}</p>
            )}
          </div>
        </div>
      </div>
    </Layout>
  );
}
